import { useEffect } from "react";
import { trackConversion } from "@/lib/conversions";

/**
 * Site-wide conversion listener.
 *
 * One delegated click handler (passive, capture-phase) covers every CTA on the
 * site instead of wiring props through dozens of components:
 *  - `tel:` links      -> call_click
 *  - `mailto:` links   -> email_click
 *  - `[data-cta]`      -> cta_click (label from the attribute or link text)
 *  - links to /contact -> cta_click
 */
export function ConversionTracker() {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      const el = target?.closest?.("a,button") as HTMLAnchorElement | HTMLButtonElement | null;
      if (!el) return;

      const href = el instanceof HTMLAnchorElement ? el.getAttribute("href") ?? "" : "";
      const label =
        el.getAttribute("data-cta") ||
        el.getAttribute("aria-label") ||
        (el.textContent ?? "").trim().slice(0, 160);

      if (href.startsWith("tel:")) {
        trackConversion("call_click", { label: href.replace("tel:", "") });
        return;
      }
      if (href.startsWith("mailto:")) {
        trackConversion("email_click", { label: href.replace("mailto:", "") });
        return;
      }
      if (el.hasAttribute("data-cta") || href.startsWith("/contact")) {
        trackConversion("cta_click", { label: label || href });
      }
    }

    document.addEventListener("click", onClick, { capture: true, passive: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
